/**
 * SCR-T01 — Teachers/Staff list (admin + manager).
 * Displays staff with role=teacher by default; admin can also see managers.
 * Table: staffCode, fullName, branch, specialization, phone/email, isActive.
 * Create via dialog (POST /api/staff — account-service sends invite email).
 * Edit via PATCH /api/staff/:id (no email/branchId change).
 */
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Plus, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { StaffForm, type StaffFormValues } from '@client/components/forms/staff-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  type StaffProfile,
  type StaffRole,
  staffKeys,
  useCreateStaff,
  useStaffList,
} from '@client/lib/queries/use-staff';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/admin/teachers/')({
  component: TeachersListPage,
});

const ROLE_LABEL: Record<StaffRole, string> = {
  admin: 'Quản trị',
  manager: 'Quản lý',
  teacher: 'Giáo viên',
};

function TeachersListPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();
  const { selectedBranchId } = useBranchFilter();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('teacher');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<StaffProfile | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    branchId: effectiveBranch,
    role: roleFilter !== 'all' ? (roleFilter as StaffRole) : undefined,
  };

  const { data, isLoading, isError, refetch } = useStaffList(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const createStaff = useCreateStaff();

  function openCreate() {
    setEditStaff(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(s: StaffProfile) {
    setEditStaff(s);
    setFormError(null);
    setDialogOpen(true);
  }

  async function handleSubmit(values: StaffFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      if (editStaff) {
        // PATCH — exclude email + branchId (locked after creation)
        const { email: _email, branchId: _branchId, ...patch } = values;
        await api.patch(`/api/staff/${editStaff.id}`, patch);
        await qc.invalidateQueries({ queryKey: staffKeys.all });
        toast.success('Đã cập nhật hồ sơ giáo viên.');
      } else {
        await createStaff.mutateAsync({
          ...values,
          phone: values.phone || undefined,
          specialization: values.specialization || undefined,
        });
        toast.success('Đã tạo tài khoản. Email mời đã được gửi.');
      }
      setDialogOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setFormError('Mã nhân viên hoặc email đã tồn tại.');
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleToggleActive(s: StaffProfile) {
    try {
      await api.patch(`/api/staff/${s.id}`, { isActive: !s.isActive });
      await qc.invalidateQueries({ queryKey: staffKeys.all });
      toast.success(s.isActive ? 'Đã vô hiệu hóa tài khoản.' : 'Đã kích hoạt tài khoản.');
    } catch (err) {
      toast.error(isApiError(err) ? err.message : 'Lỗi khi cập nhật trạng thái.');
    }
  }

  const columns: ColumnDef<StaffProfile>[] = [
    {
      accessorKey: 'staffCode',
      header: 'Mã NV',
      cell: ({ row }) => (
        <span className="font-mono text-sm text-primary">{row.original.staffCode}</span>
      ),
    },
    {
      accessorKey: 'fullName',
      header: 'Họ tên',
      cell: ({ row }) => <span className="font-medium">{row.original.fullName}</span>,
    },
    {
      accessorKey: 'role',
      header: 'Vai trò',
      cell: ({ row }) => (
        <Badge variant="secondary" className="text-xs">
          {ROLE_LABEL[row.original.role] ?? row.original.role}
        </Badge>
      ),
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => row.original.branchName ?? <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: 'specialization',
      header: 'Chuyên môn',
      cell: ({ row }) => row.original.specialization ?? <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: 'phone',
      header: 'Liên hệ',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-xs">
          {row.original.phone && <span>{row.original.phone}</span>}
          {row.original.email && (
            <span className="text-muted-foreground">{row.original.email}</span>
          )}
          {!row.original.phone && !row.original.email && (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'secondary'} className="text-xs">
          {row.original.isActive ? 'Đang hoạt động' : 'Vô hiệu'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEdit(row.original)}>
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={row.original.isActive ? 'text-destructive focus:text-destructive' : ''}
              onClick={() => void handleToggleActive(row.original)}
            >
              <PowerOff className="h-4 w-4 mr-2" />
              {row.original.isActive ? 'Vô hiệu hóa' : 'Kích hoạt lại'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Giáo viên</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={openCreate}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Thêm giáo viên
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có giáo viên nào."
        searchPlaceholder="Tìm tên/mã nhân viên..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {role === 'admin' && (
              <>
                <Select
                  value={branchFilter}
                  onValueChange={(v) => { setBranchFilter(v); setPage(1); }}
                >
                  <SelectTrigger className="h-10 w-[160px]">
                    <SelectValue placeholder="Cơ sở" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tất cả cơ sở</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={roleFilter}
                  onValueChange={(v) => { setRoleFilter(v); setPage(1); }}
                >
                  <SelectTrigger className="h-10 w-[140px]">
                    <SelectValue placeholder="Vai trò" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tất cả</SelectItem>
                    <SelectItem value="teacher">Giáo viên</SelectItem>
                    <SelectItem value="manager">Quản lý</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editStaff ? 'Sửa hồ sơ giáo viên' : 'Thêm giáo viên'}
            </DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <StaffForm
            isEdit={!!editStaff}
            isSubmitting={isSubmitting}
            branches={branches}
            actorRole={role}
            defaultValues={
              editStaff
                ? {
                    staffCode: editStaff.staffCode,
                    fullName: editStaff.fullName,
                    role: editStaff.role,
                    branchId: editStaff.branchId,
                    specialization: editStaff.specialization ?? '',
                    phone: editStaff.phone ?? '',
                    email: editStaff.email ?? '',
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
